import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExpedientesContext = createContext();

/**
 * Expedientes — replaces the legacy flat planner. The first
 * `fetchExpedientes()` triggers the backend's idempotent migration
 * from `pjr_planners` so users with saved items from the previous
 * UX find them under a "Sin clasificar" expediente automatically.
 */
export function ExpedientesProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !!user;
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchExpedientes = useCallback(async () => {
    // Auth-gated — public pages keep an empty list and avoid the 401.
    if (!isAuthed) {
      setExpedientes([]);
      return [];
    }
    try {
      const r = await axios.get(`${API_URL}/api/pjr/expedientes`, { withCredentials: true });
      setExpedientes(r.data.expedientes || []);
      return r.data.expedientes || [];
    } catch (e) {
      return [];
    }
  }, [isAuthed]);

  useEffect(() => {
    if (authLoading) return;
    fetchExpedientes();
  }, [authLoading, fetchExpedientes]);

  const createExpediente = async (payload) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/expedientes`, payload, { withCredentials: true });
      await fetchExpedientes();
      return { success: true, expediente: r.data.expediente };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo crear' };
    } finally { setLoading(false); }
  };

  const updateExpediente = async (id, patch) => {
    setLoading(true);
    try {
      const r = await axios.put(`${API_URL}/api/pjr/expedientes/${id}`, patch, { withCredentials: true });
      await fetchExpedientes();
      return { success: true, expediente: r.data.expediente };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo actualizar' };
    } finally { setLoading(false); }
  };

  const deleteExpediente = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/pjr/expedientes/${id}`, { withCredentials: true });
      await fetchExpedientes();
      return { success: true };
    } catch (e) {
      return { success: false };
    } finally { setLoading(false); }
  };

  /**
   * Add a web-quoted item. When `expediente_id` is null the backend
   * creates a new expediente named `new_expediente_name` and drops
   * the item there.
   */
  const addWebItem = async ({ expediente_id, new_expediente_name, slug, departure_date, category, adults, children, infants, child_ages, extension_id, note }) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/expedientes/items/web`, {
        expediente_id, new_expediente_name,
        slug, departure_date, category, adults, children, infants, child_ages, extension_id, note,
      }, { withCredentials: true });
      await fetchExpedientes();
      return { success: true, expediente: r.data.expediente, item_id: r.data.added_item_id };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo guardar' };
    } finally { setLoading(false); }
  };

  const addManualItem = async (payload) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/expedientes/items/manual`, payload, { withCredentials: true });
      await fetchExpedientes();
      return { success: true, expediente: r.data.expediente };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo agregar' };
    } finally { setLoading(false); }
  };

  /**
   * Add a live hotel offer (universal_id + rate_key) into an expediente.
   * `expediente_id` null creates a new expediente named `new_expediente_name`.
   */
  const addHotelItem = async (payload) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/expedientes/items/hotel`, payload, { withCredentials: true });
      await fetchExpedientes();
      return { success: true, expediente: r.data.expediente, item_id: r.data.added_item_id };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo guardar el hotel' };
    } finally { setLoading(false); }
  };

  const removeItem = async (expediente_id, item_id) => {
    try {
      await axios.delete(`${API_URL}/api/pjr/expedientes/${expediente_id}/items/${item_id}`, { withCredentials: true });
      await fetchExpedientes();
      return { success: true };
    } catch (e) {
      return { success: false };
    }
  };

  const reorderItems = async (expediente_id, option_id, item_ids) => {
    try {
      await axios.post(`${API_URL}/api/pjr/expedientes/${expediente_id}/items/reorder`,
        { option_id, item_ids }, { withCredentials: true });
      await fetchExpedientes();
      return { success: true };
    } catch (e) {
      return { success: false };
    }
  };

  const addOption = async (expediente_id, name) => {
    try {
      const r = await axios.post(`${API_URL}/api/pjr/expedientes/${expediente_id}/options`,
        { name }, { withCredentials: true });
      await fetchExpedientes();
      return { success: true, option_id: r.data.option_id };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo crear la opción' };
    }
  };

  const deleteOption = async (expediente_id, option_id) => {
    try {
      await axios.delete(`${API_URL}/api/pjr/expedientes/${expediente_id}/options/${option_id}`, { withCredentials: true });
      await fetchExpedientes();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo eliminar' };
    }
  };

  // Aggregate counts for the navbar badge — items across every
  // expediente's active option (the figure agents care about most).
  const totalItemCount = expedientes.reduce((acc, e) => {
    const opt = (e.options || []).find((o) => o.option_id === e.active_option_id) || (e.options || [])[0];
    return acc + ((opt?.items || []).length);
  }, 0);

  return (
    <ExpedientesContext.Provider value={{
      expedientes, loading, fetchExpedientes,
      createExpediente, updateExpediente, deleteExpediente,
      addWebItem, addManualItem, addHotelItem, removeItem, reorderItems,
      addOption, deleteOption,
      totalItemCount,
    }}>
      {children}
    </ExpedientesContext.Provider>
  );
}

export const useExpedientes = () => {
  const ctx = useContext(ExpedientesContext);
  if (!ctx) throw new Error('useExpedientes must be used within an ExpedientesProvider');
  return ctx;
};
